import {
    AnchorButton,
    Button,
    Card,
    Intent,
    OverlayToaster,
    Position,
    Tab,
    Tabs,
    Tooltip,
    type TabId
} from "@blueprintjs/core";
import { CardStack } from "components/common/CardStack";
import { FieldLabel } from "components/common/FieldLabel";
import { FileUpload } from "components/common/FileUpload";
import { NiraDialog } from "components/common/NiraDialog";
import { NiraTextInput } from "components/common/NiraTextInput";
import {
    In_Queue_Model_Variants_Enum,
    useGetRegionModelVariantsQuery
} from "generated/graphql";
import { useCluster, useRegionConfig } from "in_queue/contexts/ClusterContext";
import { useMemo, useState } from "react";
import { Accept } from "react-dropzone";
import css from "./AddCustomUpgradesDialog.module.scss";
import {
    AcceptedFileType,
    CustomUpgradeFileInput,
    MAX_UPLOAD_BYTES,
    useCreateCustomUpgrade
} from "./useCreateCustomUpgrade";

/** A model variant available for the current cluster's region. */
type RegionModelVariant = {
    readonly variant: In_Queue_Model_Variants_Enum;
    readonly description: string;
};

const ACCEPTED_FILE_TYPES: Accept = {
    "application/octet-stream": Object.values(AcceptedFileType).map(
        (fileType) => `.${fileType}`
    )
};

const AddCustomUpgradeToaster = OverlayToaster.create({
    position: Position.TOP
});

const showError = (message: string) => {
    AddCustomUpgradeToaster.show({ intent: Intent.DANGER, message });
};

const getFileType = (fileName: string): AcceptedFileType | null => {
    const fileType = Object.values(AcceptedFileType).find((fileType) =>
        fileName.toLowerCase().endsWith(fileType)
    );

    return fileType || null;
};

const SINGLE_FILE_TAB_ID = "single-model-custom-upgrade-upload";
const MODEL_SPECIFIC_TAB_ID = "model-specific-custom-upgrade-upload";

interface AddCustomUpgradesDialogProps {
    isOpen: boolean;
    onClose: () => void;
}

const SingleModelCustomUpgradeUpload: React.FC<{
    selectedFile: File | null;
    setSelectedFile: (file: File | null) => void;
}> = ({ selectedFile, setSelectedFile }) => {
    return (
        <div className={css["AddCustomUpgradesDialog-upload-section-content"]}>
            <FileUpload
                file={selectedFile}
                onFileSelected={setSelectedFile}
                onClearFile={() => setSelectedFile(null)}
                accept={ACCEPTED_FILE_TYPES}
                maxSize={MAX_UPLOAD_BYTES}
                onReject={showError}
                prompt="Drop upgrade file or click to browse"
            />
        </div>
    );
};

const ModelSpecificCustomUpgradeUpload: React.FC<{
    modelVariants: ReadonlyArray<RegionModelVariant>;
    modelSpecificFiles: Record<string, File | null>;
    setModelSpecificFiles: React.Dispatch<
        React.SetStateAction<Record<string, File | null>>
    >;
}> = ({ modelVariants, modelSpecificFiles, setModelSpecificFiles }) => {
    return (
        <CardStack>
            {modelVariants.map((modelVariant) => {
                const selectedFileForVariant =
                    modelSpecificFiles[modelVariant.variant];
                const setSelectedFileForVariant = (file: File | null) => {
                    setModelSpecificFiles({
                        ...modelSpecificFiles,
                        [modelVariant.variant]: file
                    });
                };

                return (
                    <Card
                        key={modelVariant.variant}
                        compact
                        className={css["AddCustomUpgradesDialog-row-card"]}
                    >
                        <div
                            className={css["AddCustomUpgradesDialog-row-model"]}
                        >
                            <strong>{modelVariant.description}</strong>
                        </div>
                        <div
                            className={css["AddCustomUpgradesDialog-row-file"]}
                        >
                            <FileUpload
                                size="small"
                                file={selectedFileForVariant ?? null}
                                onFileSelected={setSelectedFileForVariant}
                                onClearFile={() =>
                                    setSelectedFileForVariant(null)
                                }
                                accept={ACCEPTED_FILE_TYPES}
                                maxSize={MAX_UPLOAD_BYTES}
                                onReject={showError}
                                prompt="Drop or browse"
                            />
                        </div>
                    </Card>
                );
            })}
        </CardStack>
    );
};

export const AddCustomUpgradesDialog: React.FC<
    AddCustomUpgradesDialogProps
> = ({ isOpen, onClose }) => {
    const regionConfig = useRegionConfig();
    const cluster = useCluster();
    const [upgradeName, setUpgradeName] = useState<string>("");
    const [notes, setNotes] = useState<string>("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [modelSpecificFiles, setModelSpecificFiles] = useState<
        Record<string, File | null>
    >({});
    const [selectedTabId, setSelectedTabId] =
        useState<TabId>(SINGLE_FILE_TAB_ID);

    const { data: regionModelVariantsData } = useGetRegionModelVariantsQuery({
        variables: { region: cluster.region }
    });

    const modelVariants = useMemo<ReadonlyArray<RegionModelVariant>>(
        () =>
            (regionModelVariantsData?.in_queue_region_model_variants ?? []).map(
                (regionVariant) => ({
                    variant: regionVariant.variant,
                    description: regionVariant.model_variant.description
                })
            ),
        [regionModelVariantsData]
    );

    const { createUpgrade, submitting } = useCreateCustomUpgrade();

    const showModelSpecificFileUpload =
        regionConfig.customUpgrades.modelSpecificCustomUpgrades &&
        modelVariants.length > 1;

    const isModelSpecificTab =
        showModelSpecificFileUpload && selectedTabId === MODEL_SPECIFIC_TAB_ID;

    const nameProvided = upgradeName.trim() !== "";

    const allVariantFilesSelected =
        modelVariants.length > 0 &&
        modelVariants.every((variant) => modelSpecificFiles[variant.variant]);

    const canSubmit = isModelSpecificTab
        ? nameProvided && allVariantFilesSelected
        : nameProvided && selectedFile !== null;

    const buildFileInputs =
        (): ReadonlyArray<CustomUpgradeFileInput> | null => {
            const entries: ReadonlyArray<{
                readonly file: File | null | undefined;
                readonly variant: In_Queue_Model_Variants_Enum | null;
            }> = isModelSpecificTab
                ? modelVariants.map((variant) => ({
                      file: modelSpecificFiles[variant.variant],
                      variant: variant.variant
                  }))
                : [{ file: selectedFile, variant: null }];

            const fileInputs: CustomUpgradeFileInput[] = [];
            for (const { file, variant } of entries) {
                if (file === null || file === undefined) {
                    return null;
                }

                const fileType = getFileType(file.name);
                if (fileType === null) {
                    showError("Failed to add upgrade: invalid file format");
                    return null;
                }

                fileInputs.push({ file, fileType, variant });
            }
            return fileInputs;
        };

    const onSubmit = async () => {
        if (!canSubmit) return;

        const fileInputs = buildFileInputs();
        if (fileInputs === null || fileInputs.length === 0) return;

        const submittedName = upgradeName.trim();
        const trimmedNotes = notes.trim();

        const result = await createUpgrade({
            name: submittedName,
            notes: trimmedNotes !== "" ? trimmedNotes : undefined,
            files: fileInputs
        });

        if (result.ok) {
            AddCustomUpgradeToaster.show({
                intent: Intent.SUCCESS,
                message: `${submittedName} added.`
            });
            onClose();
        } else {
            showError(result.message);
        }
    };

    const singleModelFileUpload = (
        <SingleModelCustomUpgradeUpload
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
        />
    );

    const modelSpecificFileUpload = (
        <ModelSpecificCustomUpgradeUpload
            modelVariants={modelVariants}
            modelSpecificFiles={modelSpecificFiles}
            setModelSpecificFiles={setModelSpecificFiles}
        />
    );

    const tabs = (
        <Tabs
            className={css["AddCustomUpgradesDialog-tabs"]}
            selectedTabId={selectedTabId}
            onChange={(newTabId) => setSelectedTabId(newTabId)}
            renderActiveTabPanelOnly
            size="large"
        >
            <Tab
                id={SINGLE_FILE_TAB_ID}
                title="One file for all models"
                panel={singleModelFileUpload}
                panelClassName={css["AddCustomUpgradesDialog-tab-panel"]}
            />
            <Tab
                id={MODEL_SPECIFIC_TAB_ID}
                title="Different file per model"
                panel={modelSpecificFileUpload}
                panelClassName={css["AddCustomUpgradesDialog-tab-panel"]}
            />
        </Tabs>
    );

    return (
        <NiraDialog
            isOpen={isOpen}
            onClose={onClose}
            eyebrow="New Upgrade"
            title="Add Portfolio Upgrade"
            className={css["AddCustomUpgradesDialog"]}
            gap="small"
            footerActions={
                <>
                    <Button
                        text="Cancel"
                        onClick={onClose}
                        disabled={submitting}
                        size="large"
                    />
                    <Tooltip
                        content="Add an upgrade name and select a file"
                        position={Position.TOP_RIGHT}
                        disabled={canSubmit}
                    >
                        <AnchorButton
                            text="Upload and validate"
                            intent={Intent.PRIMARY}
                            disabled={!canSubmit || submitting}
                            loading={submitting}
                            onClick={onSubmit}
                            size="large"
                        />
                    </Tooltip>
                </>
            }
        >
            <NiraTextInput
                id="portfolio-upgrade-name-input"
                label="Upgrade Name"
                placeholder="e.g. Brunswick transformer alternative"
                value={upgradeName}
                onChange={setUpgradeName}
            />
            <NiraTextInput
                multiline
                id="portfolio-upgrade-notes-input"
                label="Notes (optional)"
                placeholder="Add context for this upgrade..."
                value={notes}
                onChange={setNotes}
            />

            {showModelSpecificFileUpload ? (
                tabs
            ) : (
                <div>
                    <FieldLabel>Upgrade File</FieldLabel>
                    {singleModelFileUpload}
                </div>
            )}
        </NiraDialog>
    );
};
